import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtCurrencyCompact, fmtNumber, WOCHENTAGE_LANG } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { ChartSkeleton } from '../components/Skeleton.tsx';

const STUNDEN = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const WT_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function HeatmapChart() {
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const { data, isLoading } = useQuery({
    queryKey: ['heatmap', filters],
    queryFn: () => api.heatmap({ filters }),
  });

  if (isLoading) return <ChartSkeleton height={280} />;

  const cells: Array<[number, number, number, number]> = (data ?? []).map((c) => [
    c.stunde,
    c.wochentag - 1,
    c.umsatz,
    c.anz_bons,
  ]);
  const max = Math.max(...cells.map((c) => c[2]), 1);

  const option = {
    grid: { top: 10, right: 20, bottom: 60, left: 50, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      position: 'top',
      formatter: (p: { value: [number, number, number, number] }) => {
        const [stunde, wt, umsatz, bons] = p.value;
        return `<div style="font-size:12px">
          <div style="font-weight:600;margin-bottom:4px">${WOCHENTAGE_LANG[wt + 1]} ${String(stunde).padStart(2, '0')}:00</div>
          <div style="font-variant-numeric:tabular-nums">${fmtCurrency(umsatz)}</div>
          <div style="color:#94a3b8;font-size:11px;margin-top:2px">${fmtNumber(bons)} Bons</div>
        </div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: STUNDEN,
      ...axisStyle(),
      splitArea: { show: false },
      axisLabel: { ...axisStyle().axisLabel, formatter: (v: string) => `${v}h` },
    },
    yAxis: {
      type: 'category',
      data: WT_KURZ,
      ...axisStyle(),
      axisLine: { show: false },
      splitArea: { show: false },
    },
    visualMap: {
      min: 0,
      max,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 14,
      itemHeight: 180,
      text: ['Hoch', 'Niedrig'],
      textStyle: { color: COLORS.textMuted, fontSize: 11 },
      formatter: (v: number) => fmtCurrencyCompact(v),
      inRange: {
        color: ['#eff6ff', '#bfdbfe', '#60a5fa', '#1f97f5', '#0b62a8', '#072845'],
      },
    },
    series: [
      {
        type: 'heatmap',
        data: cells.map((c) => [c[0], c[1], c[2]]),
        itemStyle: {
          borderRadius: 3,
          borderColor: '#fff',
          borderWidth: 2,
        },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(15,23,42,0.3)' },
        },
        progressive: 1000,
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 280 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
      onEvents={{
        click: (params: { value: [number, number, number] }) => {
          const [stunde, wt] = params.value;
          addFilter('wochentag', wt + 1);
          addFilter('stunde', stunde);
        },
      }}
    />
  );
}
